import React, { useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { TrendingUp, Users, X, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/* ================= MOCK DATA ================= */

const revenueData = [
  { month: "Jan", revenue: 120 },
  { month: "Feb", revenue: 190 },
  { month: "Mar", revenue: 240 },
  { month: "Apr", revenue: 310 },
  { month: "May", revenue: 400 },
  { month: "Jun", revenue: 450 },
];

const SubscriptionDashboard = () => {
  const currentPlan = "Professional";
  const monthlyOrders = 3200;
  const maxOrders = 5000;
  const maxUsers = 15; // Professional plan limit

  const usagePercent = Math.round((monthlyOrders / maxOrders) * 100);

  const [users, setUsers] = useState([
    { name: "Admin User", email: "admin@teqa.com", role: "Admin" },
  ]);

  const [showModal, setShowModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "Member",
  });

  const handleAddUser = () => {
    if (users.length >= maxUsers) return;
    if (!newUser.name || !newUser.email) return;

    setUsers([...users, newUser]);
    setNewUser({ name: "", email: "", role: "Member" });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto space-y-10">

        {/* Header */}
        <div>
          <h1 className="text-3xl font-semibold text-gray-800 dark:text-slate-100">
            Subscription Dashboard
          </h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1">
            Monitor your subscription, usage & billing
          </p>
        </div>

        {/* Active Plan */}
        <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">
                Current Plan: {currentPlan}
              </h2>
              <p className="text-gray-500 dark:text-slate-400 mt-1">
                Next billing: 25 July 2025
              </p>
            </div>

            <div className="flex gap-4 items-center">
              <span className="px-4 py-2 bg-green-100 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400 rounded-full text-sm font-medium">
                Active
              </span>

              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl hover:bg-indigo-700 transition"
              >
                <Users size={16} />
                Manage Users
              </button>
            </div>
          </div>

          {/* Usage */}
          <div className="mt-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-slate-400 mb-2">
              <span>Orders Usage</span>
              <span>{monthlyOrders} / {maxOrders}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-800 h-3 rounded-full">
              <div
                className="bg-indigo-600 h-3 rounded-full"
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          </div>

          {/* Users Count */}
          <div className="mt-6 text-sm text-gray-600 dark:text-slate-400">
            Team Members: {users.length} / {maxUsers}
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              Monthly Revenue
            </h3>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" className="dark:opacity-20" />
              <XAxis dataKey="month" stroke="currentColor" className="text-gray-500 dark:text-slate-400" />
              <YAxis stroke="currentColor" className="text-gray-500 dark:text-slate-400" />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#4f46e5"
                fill="#c7d2fe"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* ================= USERS MODAL ================= */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-2xl p-8 relative"
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
            >
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-5 right-5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
              >
                <X size={18} />
              </button>

              <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-slate-100">
                Manage Team Members
              </h2>

              {/* Existing Users */}
              <div className="space-y-3 mb-6">
                {users.map((user, index) => (
                  <div
                    key={index}
                    className="flex justify-between bg-gray-50 dark:bg-slate-800 p-3 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-100">{user.name}</p>
                      <p className="text-xs text-gray-500 dark:text-slate-500">
                        {user.email}
                      </p>
                    </div>
                    <span className="text-sm text-indigo-600 dark:text-indigo-400">
                      {user.role}
                    </span>
                  </div>
                ))}
              </div>

              {/* Add New User */}
              {users.length < maxUsers && (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={newUser.name}
                    onChange={(e) =>
                      setNewUser({ ...newUser, name: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <select
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({ ...newUser, role: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg border dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  >
                    <option value="Member">Member</option>
                    <option value="Admin">Admin</option>
                  </select>

                  <button
                    onClick={handleAddUser}
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition"
                  >
                    <Plus size={16} />
                    Add User
                  </button>
                </div>
              )}

              {users.length >= maxUsers && (
                <p className="text-sm text-red-500 dark:text-rose-400 mt-4">
                  You reached the maximum number of users for this plan.
                </p>
              )}

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionDashboard;
